/**
 * Theme Storage Service
 *
 * Platform-aware theme persistence following tokenStorage.ts pattern.
 * Uses localStorage on web, SecureStore on native platforms.
 */

import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import { ThemeId } from '../theme/types';

const THEME_KEY = 'app_theme_id';

// Check if running on web
const isWeb = Platform.OS === 'web';

/**
 * Web storage fallback using localStorage
 */
const webStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(key) || null;
    } catch {
      return null;
    }
  },
  async setItem(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  },
  async deleteItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Error removing from localStorage:', e);
    }
  },
};

/**
 * Store the selected theme ID.
 */
export async function storeTheme(themeId: ThemeId): Promise<void> {
  const themeIdStr = JSON.stringify(themeId);

  if (isWeb) {
    await webStorage.setItem(THEME_KEY, themeIdStr);
  } else {
    await SecureStore.setItemAsync(THEME_KEY, themeIdStr);
  }
}

/**
 * Retrieve the stored theme ID.
 * Returns 'bauhaus' as default if not found.
 */
export async function getStoredTheme(): Promise<ThemeId> {
  let themeIdStr: string | null;

  if (isWeb) {
    themeIdStr = await webStorage.getItem(THEME_KEY);
  } else {
    themeIdStr = await SecureStore.getItemAsync(THEME_KEY);
  }

  if (!themeIdStr) {
    return 'bauhaus'; // Default theme
  }

  try {
    const themeId = JSON.parse(themeIdStr) as ThemeId;

    // Validate theme ID
    if (themeId === 'bauhaus' || themeId === 'kinetic') {
      return themeId;
    }

    // Fallback to default if invalid
    return 'bauhaus';
  } catch {
    return 'bauhaus';
  }
}

/**
 * Remove the stored theme ID.
 */
export async function removeTheme(): Promise<void> {
  if (isWeb) {
    await webStorage.deleteItem(THEME_KEY);
  } else {
    await SecureStore.deleteItemAsync(THEME_KEY);
  }
}
